/**
 * ArchLang v0.10 — view projection stage, scene layer (S13b).
 *
 * `applyProjection(sceneModules, edges, plan)` applies an engine
 * {@link ViewProjectionPlan} (produced by `@archlang/engine`'s `planView`) to the
 * board's scene `Module[]` / `DependencyEdge[]`, yielding the filtered, re-parented,
 * edge-lifted inputs the board layout (`flattenModulesWithParent` /
 * `IncrementalFlatten`) consumes. `group by` emits FLAT (frame groups): members
 * and copies land at root and {@link ProjectionResult.groupFrames} names each
 * group's board ids — the layout solves them under a forced partition and the
 * board draws background frames, replacing the old nested-container rendering.
 * Pure — no DOM, no layout, no engine dependency.
 *
 * ## Identity projection
 * `plan === null` (the default board) returns its inputs **unchanged by reference**
 * — `{ modules: sceneModules, edges }`. This is the allocation-cheap identity path;
 * the byte-identical-board guarantee is proven by the S15 snapshot gate, not by this
 * reference pass-through.
 *
 * ## The engine↔scene key bridge (BLOCKER-1)
 * The plan is keyed on the **dotted qualified name** (what `evalSelector` returns).
 * A scene `Module` is matched back to its qualified key via `qualifiedKeyByModule`
 * (stamped here up-front) — NOT the id-first `moduleKey`. The two diverge for every
 * `#id`-carrying module, so an id-carrying module is matched by qualified key and
 * kept, never silently dropped. Original modules keep their `id` on the clone (so
 * the board's id-keyed seams stay stable); group copies get their synthetic copy id.
 *
 * ## The plan type is mirrored, not imported
 * `@archlang/scene` deliberately does not depend on `@archlang/engine` (same as
 * `Module`/`DependencyEdge` mirroring the resolved-model shapes). The
 * {@link ViewProjectionPlan} below is a structural mirror of the engine type; the
 * engine plan is assignable to it. Keep the two in sync — especially
 * {@link edgeIdOf}, which must produce the byte-identical string on both sides.
 */

import type { DependencyEdge, FieldEntry, Module, StableId } from "./model.js";
import { qualifiedKeyByModule, stampQualifiedKeys, walkModules } from "./utils.js";
import { paletteColor } from "../palette.js";

// =============================================================================
// Mirrored plan shape (see module doc — mirror of @archlang/engine)
// =============================================================================

export interface LiftedEdge {
  readonly edgeId: string;
  readonly fromKey: string;
  readonly toKey: string;
  readonly toInterfaceKey: string;
  readonly via: readonly string[];
  readonly fromId?: string;
  readonly toId?: string;
  readonly origin: "via-lift" | "group-copy";
}

export interface GroupAssignment {
  readonly containerId: string;
  readonly label: string;
  readonly memberKeys: readonly string[];
  readonly copies: readonly { readonly copyId: string; readonly originKey: string }[];
  /** Nested `group by`: the parent group's containerId (absent = top level). */
  readonly parentId?: string;
}

export interface NodeStyle {
  readonly color?: string;
  readonly rename?: string;
  readonly pin?: { readonly x: number; readonly y: number };
  readonly overlay?: ReadonlyMap<string, string>;
}

/** Mirror of the engine `EdgeStyle` (renamed to avoid a barrel clash with
 *  `widget-spec`'s `EdgeStyle`). */
export interface ProjEdgeStyle {
  readonly color?: string;
  readonly width?: number;
}

export interface BoundaryStub {
  readonly peerKey: string;
  readonly direction: "in" | "out";
  readonly count: number;
  /** `style … outside` overlay for this stub (VIEWS §7 third form). */
  readonly style?: { readonly color?: string; readonly width?: number };
}

export interface BoundaryMark {
  readonly nodeKey: string;
  readonly stubs: readonly BoundaryStub[];
}

/** Mirror of the engine `ColorLegendEntry` (S14 style engine). A `swatch` /
 *  `noData` row carries a flat `color`; a `gradient` row carries its ramp
 *  endpoints + axis labels. `memberKeys` are qualified keys — bridge them to
 *  board node ids via {@link ProjectionResult.nodeIdsByOrigin}. */
export interface ViewLegendEntry {
  readonly id: string;
  readonly label: string;
  readonly color?: string;
  readonly gradient?: {
    readonly from: string;
    readonly to: string;
    readonly minLabel: string;
    readonly maxLabel: string;
  };
  readonly kind: "swatch" | "gradient" | "noData";
  readonly memberKeys: readonly string[];
}

/** Loose mirror of the engine `Diagnostic` — only what the shell surfaces for
 *  the style engine's lints (unknown bundle/policy, css-unparseable, …). */
export interface ViewStyleDiagnostic {
  readonly severity: string;
  readonly message: string;
  readonly code?: string;
}

export interface ViewProjectionPlan {
  readonly shown: ReadonlySet<string>;
  readonly hidden: ReadonlySet<string>;
  readonly focus: ReadonlySet<string>;
  /** Planes this projection narrowed to (§7) — empty when it didn't. Members
   *  carry their own presence; `shown` can't express a field's. */
  readonly planes: ReadonlySet<string>;
  /** Member keys the DEFAULT plane hides — what a partial-presence block
   *  declared. Empty for a plane PICK, which answers through `planes`. */
  readonly planeHiddenMembers?: ReadonlySet<string>;
  readonly parentOf: ReadonlyMap<string, string | null>;
  readonly groups: readonly GroupAssignment[];
  readonly ungrouped: ReadonlySet<string>;
  readonly copyOrigin: ReadonlyMap<string, string>;
  readonly edgeLift: { readonly drop: ReadonlySet<string>; readonly add: readonly LiftedEdge[] };
  readonly styleIndex: ReadonlyMap<string, NodeStyle>;
  readonly edgeStyleIndex: ReadonlyMap<string, ProjEdgeStyle>;
  readonly boundary: readonly BoundaryMark[];
  readonly stableIds: ReadonlyMap<string, StableId>;
  /** S14 color casts: qualified key → resolved CSS color. NOT baked onto
   *  modules here — a stroke overlay the shell routes through `NodeColoring`. */
  readonly colorResult: ReadonlyMap<string, string>;
  /** Ordered legend rows for {@link colorResult} — the legend never lies. */
  readonly legend: readonly ViewLegendEntry[];
  /** `pin <x>,<y>` directives: qualified key → board position. Fed into the
   *  layout `placements` channel (the one drags write) — not applied here. */
  readonly pins: ReadonlyMap<string, { readonly x: number; readonly y: number }>;
  /** Style-engine lint diagnostics (surfaced by the shell; empty otherwise). */
  readonly diagnostics: readonly ViewStyleDiagnostic[];
  /** The view's declared `at` point, if it wrote one — what a view MEANS by
   *  naming a moment. The shell seeds the scrubber from it when the view becomes
   *  active; without it here, `at v3` reaches the plan and stops, and a
   *  `Current` and a `ToBe` view over one board paint identically. */
  readonly at?: ScrubSpec;
}

/** A view's `at now` / `at v3` / `at now..v3` — mirrors the engine's
 *  `AtClause` + `ScrubPoint`. */
export interface ScrubSpec {
  readonly at: ScrubMoment;
  readonly compare?: ScrubMoment;
}

export type ScrubMoment =
  | { readonly kind: "now" }
  | { readonly kind: "marker"; readonly text: string };

/** One `group by` frame: the flat-emit replacement for the old synthetic
 *  container. Members render at ROOT; the board draws a background frame
 *  around them post-solve (BoardSpace.groupFrames). `memberBoardIds` are
 *  final board node ids (real `#id`, `::m::` synthetic, or `g<i>::` copy id)
 *  — no key bridging downstream. */
export interface GroupFrameSpec {
  /** The plan's containerId (`::group::<path>`) — reserved scheme. */
  readonly id: string;
  readonly label: string;
  /** DIRECT members only (a parent's inner-less pool); descendants live in
   *  child specs linked via `parentId`. */
  readonly memberBoardIds: readonly string[];
  /** Nested `group by`: parent spec id (absent = top level). */
  readonly parentId?: string;
}

/** The result of {@link applyProjection}. `boundary`, `nodeIdsByOrigin`, and
 *  `groupFrames` are present only under a plan (frames only when it groups). */
export interface ProjectionResult {
  readonly modules: readonly Module[];
  readonly edges: readonly DependencyEdge[];
  readonly boundary?: readonly BoundaryMark[];
  /**
   * Origin qualified key → the board node id(s) rendering it: the clone's `#id`
   * when it carries one, else its synthetic `::m::` id for a grouped id-less
   * member, else its PROJECTED qualified key (what the flatten stamps over
   * this tree — re-parenting changes it), and EVERY copy id for a copied
   * origin. This is the one bridge the style outputs (colorResult / legend /
   * pins / focus — all keyed on ORIGIN qualified keys) use to reach id-keyed
   * board seams.
   */
  readonly nodeIdsByOrigin?: ReadonlyMap<string, readonly string[]>;
  /** `group by` frames, in group order (frame-groups plan). */
  readonly groupFrames?: readonly GroupFrameSpec[];
}

// =============================================================================
// Edge identity — the scene mirror of the engine edge key (MUST stay in sync)
// =============================================================================

/** Canonical cross-layer edge id. Byte-identical to `@archlang/engine`'s
 *  `edgeIdOf` — the same fields, the same join — so plan drop ids computed over
 *  engine edges match scene edges here. */
export function edgeIdOf(e: DependencyEdge): string {
  // JSON array of the parts — MUST match `@archlang/engine`'s `edgeIdOf` byte for
  // byte so plan drop ids (computed engine-side) match scene edges here.
  return JSON.stringify([
    e.fromModulePath.join("."),
    e.toModulePath.join("."),
    e.toInterfacePath.join("."),
    e.processName ?? null,
    e.stepIndex ?? null,
    e.altCondition ?? null,
  ]);
}

/** The logical identity of an edge/lift — its (from, to, toInterface) triple. Mirror
 *  of the engine helper; used to dedup a via-lift against a surviving real edge. */
function liftIdentity(from: string, to: string, iface: string): string {
  return JSON.stringify([from, to, iface]);
}

// =============================================================================
// applyProjection
// =============================================================================

const ROOT = null;

export function applyProjection(
  sceneModules: readonly Module[],
  edges: readonly DependencyEdge[],
  plan: ViewProjectionPlan | null
): ProjectionResult {
  // Identity projection — reference pass-through (default board).
  if (!plan) return { modules: sceneModules, edges };

  // Match scene modules to their QUALIFIED key (not id-first moduleKey) — BLOCKER-1.
  // The same walk records each key's input DFS position so siblings can be re-emitted
  // in the original scene order (BUG D) rather than lexicographic render-id order.
  stampQualifiedKeys(sceneModules);
  const byQual = new Map<string, Module>();
  const inputOrder = new Map<string, number>();
  let orderCounter = 0;
  walkModules(sceneModules, (m) => {
    const q = qualifiedKeyByModule.get(m);
    if (q === undefined) return;
    byQual.set(q, m);
    if (!inputOrder.has(q)) inputOrder.set(q, orderCounter++);
  });

  // v0.11 (D2/D5 bridge): scene stamped keys are `Space::Qualified.Path` for
  // spaced modules, while the engine plan (selector universe, parentOf,
  // groups, copyOrigin) keys by the space-BLIND origin qualified path —
  // selectors deliberately see one transparent model. Translate at the seam:
  // `bareOf` strips the space prefix for every plan lookup; `byOrigin` maps a
  // bare origin key to ALL stamped matches (two spaces' same-named modules
  // are both selected by the one origin key); `inputOrder` records the bare
  // alias so copy ordering keeps working. Spaceless models: bareOf is
  // identity and every list is a singleton — byte-identical behavior.
  const bareOf = (q: string): string => {
    const i = q.indexOf("::");
    return i >= 0 ? q.slice(i + 2) : q;
  };
  const byOrigin = new Map<string, string[]>(); // bare origin → stamped keys
  for (const q of byQual.keys()) {
    const b = bareOf(q);
    const list = byOrigin.get(b);
    if (list) list.push(q);
    else byOrigin.set(b, [q]);
    if (!inputOrder.has(b)) inputOrder.set(b, inputOrder.get(q)!);
  }
  /** Resolve a plan-side (bare) key to the stamped clone key nearest a
   *  context key's space (same `Space::` prefix wins; else first match). */
  const stampedFor = (origin: string, context?: string): string | undefined => {
    const list = byOrigin.get(origin);
    if (!list || list.length === 0) return undefined;
    if (list.length === 1 || !context) return list[0];
    const ci = context.indexOf("::");
    const cSpace = ci >= 0 ? context.slice(0, ci) : "";
    return list.find((q) => (q.indexOf("::") >= 0 ? q.slice(0, q.indexOf("::")) : "") === cSpace) ?? list[0];
  };

  const shown = plan.shown;
  const shownHas = (stamped: string): boolean =>
    shown.has(stamped) || shown.has(bareOf(stamped));
  const copiedOrigins = new Set(plan.copyOrigin.values());

  // ---- Build the render node set + desired parents --------------------------
  const clones = new Map<string, Module>(); // renderId → clone (children rebuilt)
  const desiredParent = new Map<string, string | null>(); // renderId → parent renderId | ROOT
  const groupedMembers = new Set<string>(); // qual keys claimed by a group

  // 1. grouped members and per-group copies — FLAT at root (frame groups):
  //    no synthetic containers; the board draws a frame around each group's
  //    members after the solve. An id-less member gets the reserved
  //    `::m::<originKey>` board id — at root its projected qualified key
  //    would be its bare name, and two grouped `A.Service`/`B.Service`
  //    would collide on every id-keyed seam.
  const groupFrames: {
    id: string;
    label: string;
    memberBoardIds: string[];
    parentId?: string;
  }[] = [];
  const memberBoardId = new Map<string, string>(); // origin qual key → board id
  for (const g of plan.groups) {
    const frame = {
      id: g.containerId,
      label: g.label,
      memberBoardIds: [] as string[],
      ...(g.parentId !== undefined ? { parentId: g.parentId } : {}),
    };
    for (const mk of g.memberKeys) {
      // Space-blind membership (D5): one origin key selects EVERY stamped
      // match — two spaces' same-named members both join the group.
      for (const sk of byOrigin.get(mk) ?? []) {
        const src = byQual.get(sk)!;
        const bid = src.id ?? `::m::${sk}`;
        clones.set(sk, styledClone(src, bid, mk, plan));
        desiredParent.set(sk, ROOT); // grouping overrides the shown parent
        groupedMembers.add(sk);
        memberBoardId.set(sk, bid);
        frame.memberBoardIds.push(bid);
      }
    }
    for (const { copyId, originKey } of g.copies) {
      const src = byQual.get(stampedFor(originKey) ?? originKey);
      if (!src) continue;
      clones.set(copyId, styledClone(src, copyId, originKey, plan));
      desiredParent.set(copyId, ROOT);
      frame.memberBoardIds.push(copyId);
    }
    groupFrames.push(frame);
  }
  // Prune: a frame stays if it (or any descendant) has members — an empty
  // parent whose children all pruned would draw a ghost rect.
  const hasMembers = (id: string): boolean => {
    const f = groupFrames.find((x) => x.id === id)!;
    if (f.memberBoardIds.length > 0) return true;
    return groupFrames.some((x) => x.parentId === id && hasMembers(x.id));
  };
  const prunedFrames = groupFrames.filter((f) => hasMembers(f.id));

  // 2. every other shown scene module (ungrouped / non-grouped), at its effective
  //    parent (hide mid-tree re-parent), root when the parent is not rendered.
  for (const [qual, src] of byQual) {
    const origin = bareOf(qual);
    if (!shownHas(qual)) continue;
    if (copiedOrigins.has(origin)) continue; // replaced by its per-group copies
    if (groupedMembers.has(qual)) continue; // already under a container
    if (clones.has(qual)) continue;
    clones.set(qual, styledClone(src, src.id, origin, plan));
    // The plan's parent is a bare origin key — resolve it to the stamped
    // clone key in THIS module's space (a re-parent stays within a subtree).
    const planParent = plan.parentOf.get(origin);
    desiredParent.set(
      qual,
      planParent != null ? stampedFor(planParent, qual) ?? planParent : ROOT,
    );
  }

  // ---- Assemble the tree (input-order-preserving sibling order — BUG D) ------
  // Sibling order: real modules and per-group copies at their ORIGINAL input
  // DFS position — a membership-neutral plan (shown=all, no groups) yields
  // the exact input order + parenting, keeping the default board
  // byte-identical (the S15 identity rail).
  const sortKey = (rid: string): readonly [number, string] => {
    const originKey = plan.copyOrigin.get(rid) ?? rid;
    return [
      inputOrder.get(originKey) ?? inputOrder.get(bareOf(originKey)) ?? Number.MAX_SAFE_INTEGER,
      rid,
    ];
  };
  const cmp = (a: string, b: string): number => {
    const [ra, ta] = sortKey(a);
    const [rb, tb] = sortKey(b);
    return ra - rb || ta.localeCompare(tb);
  };
  const childIds = new Map<string, string[]>();
  const rootIds: string[] = [];
  for (const rid of clones.keys()) {
    const p = desiredParent.get(rid);
    const parentId = p != null && clones.has(p) ? p : ROOT;
    if (parentId === ROOT) {
      rootIds.push(rid);
    } else {
      const kids = childIds.get(parentId) ?? [];
      kids.push(rid);
      childIds.set(parentId, kids);
    }
  }
  rootIds.sort(cmp);
  for (const kids of childIds.values()) kids.sort(cmp);
  // While assembling, record each rendered node's board id (id-first, else the
  // PROJECTED qualified name — the exact key the flatten stamps over this tree)
  // under its ORIGIN qualified key. Copies fold onto their origin; minted
  // containers have no origin and are skipped. See `ProjectionResult.nodeIdsByOrigin`.
  const nodeIdsByOrigin = new Map<string, string[]>();
  /**
   * v0.9.0 §7: partial presence gives a MEMBER a presence its node doesn't have
   * — one declared in an `on <plane> { … }` block is absent from primary, one
   * merely referenced there rides both, and a main-body member of the same node
   * doesn't ride at all. The serializer tags every member of such a node with
   * its own plane set, and the row survives iff that set meets the plane this
   * projection narrowed to.
   *
   * This can't go through `shown`: a field is a node ATTRIBUTE, not a universe
   * node, so its key is never a `shown` candidate and every tagged field would
   * drop. Members carry `planes` only when their node uses partial presence, so
   * every other model passes through by reference, byte-identical.
   */
  const planes = plan.planes;
  const onPlane = (tagged: readonly string[]): boolean => {
    if (planes.size === 0) return true; // no plane narrowing — presence is not a filter
    for (const p of tagged) if (planes.has(p)) return true;
    return false;
  };
  // The DEFAULT board asks a different question than a plane pick: keep the
  // model as written, minus what a block declared. Those members are named by
  // KEY (they have no plane a tag join could match — their set is the plane
  // they live on, and the default board is on none of them).
  const hiddenMembers: ReadonlySet<string> = plan.planeHiddenMembers ?? new Set<string>();
  const withPlanePresentMembers = (src: Module, originKey: string): Module => {
    const present = <T extends { planes?: readonly string[] }>(
      list: readonly T[],
      memberName: (x: T) => string
    ): readonly T[] => {
      const byPlane =
        list.some((x) => x.planes?.length)
          ? list.filter((x) => !x.planes?.length || onPlane(x.planes))
          : list;
      return hiddenMembers.size > 0
        ? byPlane.filter((x) => !hiddenMembers.has(`${originKey}.${memberName(x)}`))
        : byPlane;
    };
    const named = (x: { name: string }): string => x.name;
    const interfaces = present(src.interfaces, named);
    const surfaces = present(src.surfaces, named);
    // A field's key is its DOTTED path (`widget.label2`) — the engine keys it
    // the same way, which is what makes the two sides join.
    const fields = present(src.fields, (f) => f.path.join("."));
    if (interfaces === src.interfaces && surfaces === src.surfaces && fields === src.fields) return src;
    return { ...src, interfaces, surfaces, fields };
  };
  const build = (rid: string, parentQual: string | null): Module => {
    const src = clones.get(rid)!;
    // A spaced ungrouped root's rid IS its stamped key (`Space::Name`) — seed
    // the projected qualified chain with it so two spaces' same-named
    // id-less roots mint distinct board ids (matching the default board's
    // stamped-key ids). Grouped members/copies carry explicit ids instead.
    const rootBase = rid.includes("::") && !rid.startsWith("::m::") ? rid : src.name;
    const qual = parentQual === null ? rootBase : `${parentQual}.${src.name}`;
    // Origin keys are the ENGINE'S bare domain — the app's view bridge joins
    // them back to plan/selector keys.
    const originKey = bareOf(plan.copyOrigin.get(rid) ?? rid);
    const boardId = src.id ?? qual;
    const list = nodeIdsByOrigin.get(originKey);
    if (list) list.push(boardId);
    else nodeIdsByOrigin.set(originKey, [boardId]);
    return {
      ...withPlanePresentMembers(src, originKey),
      children: (childIds.get(rid) ?? []).map((kid) => build(kid, qual)),
    };
  };
  const modules = rootIds.map((rid) => build(rid, null));

  // ---- Edges: drop / keep / add --------------------------------------------
  // Record the logical (from,to,iface) identity of every real edge that survives so
  // a via-lift onto the same identity can be dropped instead of double-rendering the
  // arrow (BUG A). Only real edges seed the set — two fanned lifts sharing an
  // identity (a lift out of a copied origin) must BOTH render, so lifts never seed it.
  const outEdges: DependencyEdge[] = [];
  const keptIdentities = new Set<string>();
  for (const e of edges) {
    const id = edgeIdOf(e);
    if (plan.edgeLift.drop.has(id)) continue;
    // Bare origin domain for plan lookups; stamped domain (endpoint space
    // folded, the edgeFromKey convention) for board-id pairing.
    const fromQ = e.fromModulePath.join(".");
    const toQ = e.toModulePath.join(".");
    const fromStamped = e.fromSpace ? `${e.fromSpace}::${fromQ}` : fromQ;
    const toStamped = e.toSpace ? `${e.toSpace}::${toQ}` : toQ;
    if (!shownHas(fromStamped) || !shownHas(toStamped)) continue; // boundary / absent endpoint
    if (copiedOrigins.has(fromQ) || copiedOrigins.has(toQ)) continue; // superseded by fan-out
    // Bake a `style <edge>`/`style violating <P>` override into the edge's
    // `widgetStyle` (MAJOR-3) — the exact channel the live SpaceRenderer and the
    // headless renderer already read. A grouped endpoint additionally gets its
    // board id stamped (`fromModuleId`/`toModuleId`) so the id-first edge key
    // resolves to the flat-emitted node — path-based resolution would look for
    // the ORIGIN qualified path, which no longer names a board node. No style
    // and no grouped endpoint → the SAME edge reference passes through
    // (byte-identity of the default board holds).
    const es = plan.edgeStyleIndex.get(id);
    const fromBid = memberBoardId.get(fromStamped);
    const toBid = memberBoardId.get(toStamped);
    outEdges.push(
      es || fromBid !== undefined || toBid !== undefined
        ? {
            ...e,
            // A restyle colour arrives as a palette NAME (`colorize`/`distinct`
            // both yield names — the engine holds no hex), so it resolves to
            // pixels here, the one place a baked edge style enters the board.
            ...(es
              ? {
                  widgetStyle: {
                    ...e.widgetStyle,
                    ...es,
                    ...(es.color ? { color: paletteColor(es.color) } : {}),
                  },
                }
              : {}),
            ...(fromBid !== undefined ? { fromModuleId: fromBid } : {}),
            ...(toBid !== undefined ? { toModuleId: toBid } : {}),
          }
        : e,
    );
    keptIdentities.add(liftIdentity(fromQ, toQ, e.toInterfacePath.join(".")));
  }
  for (const le of plan.edgeLift.add) {
    if (le.origin === "via-lift" && keptIdentities.has(liftIdentity(le.fromKey, le.toKey, le.toInterfaceKey))) continue;
    // A lifted/fanned edge's plan-stamped ids cover copies and real #ids;
    // an id-less GROUPED endpoint still needs its `::m::` board id.
    const edge = sceneEdgeFromLifted(le);
    // Lifted-edge keys are engine-bare — resolve to the stamped member id.
    const fb =
      edge.fromModuleId ??
      memberBoardId.get(le.fromKey) ??
      memberBoardId.get(stampedFor(le.fromKey) ?? le.fromKey);
    const tb =
      edge.toModuleId ??
      memberBoardId.get(le.toKey) ??
      memberBoardId.get(stampedFor(le.toKey) ?? le.toKey);
    outEdges.push({
      ...edge,
      ...(fb !== undefined ? { fromModuleId: fb } : {}),
      ...(tb !== undefined ? { toModuleId: tb } : {}),
    });
  }

  return {
    modules,
    edges: outEdges,
    boundary: plan.boundary,
    nodeIdsByOrigin,
    ...(prunedFrames.length > 0 ? { groupFrames: prunedFrames } : {}),
  };
}

// =============================================================================
// Synthetic node / edge factories
// =============================================================================

/** A childless clone of a scene module carrying the given id (origin id, copy
 *  id, or a grouped member's `::m::` synthetic). */
function cloneModule(src: Module, id: StableId | undefined): Module {
  return { ...src, id, children: [] };
}

/**
 * Clone a scene module, then bake its view `style` overlay (S14, MINOR-7): a
 * `widget { … }` overlay lands as `widget.*` fields (the exact channel
 * `extractWidgetSpec` reads, so `item.meta`/the composer already carries it),
 * and `rename` lands as `displayLabel` (a render-only label — truth `name` and
 * the id-keyed seams are untouched). Color is NOT baked here (it is a stroke
 * overlay routed through S15's `NodeColoring`). `originKey` is the module's
 * qualified key (a copy resolves back to its origin) — the `styleIndex` key.
 */
function styledClone(
  src: Module,
  id: StableId | undefined,
  originKey: string,
  plan: ViewProjectionPlan
): Module {
  const clone = cloneModule(src, id);
  const style = plan.styleIndex.get(originKey);
  if (!style) return clone;

  let fields = clone.fields;
  if (style.overlay && style.overlay.size > 0) {
    const overlayPaths = new Set(style.overlay.keys());
    const kept = clone.fields.filter((f) => !overlayPaths.has(f.path.join(".")));
    const added: FieldEntry[] = [...style.overlay].map(([path, value]) => ({ path: path.split("."), value }));
    fields = [...kept, ...added];
  }
  return {
    ...clone,
    ...(fields !== clone.fields ? { fields } : {}),
    ...(style.rename !== undefined ? { displayLabel: style.rename } : {}),
  };
}

/** A synthesized board edge from a plan `LiftedEdge` (via-lift or group-copy). The
 *  `fromId`/`toId` stamp the id-first edge key so it matches the copy / id module. */
function sceneEdgeFromLifted(le: LiftedEdge): DependencyEdge {
  return {
    fromModulePath: le.fromKey.split("."),
    fromModuleId: le.fromId,
    toModulePath: le.toKey.split("."),
    toModuleId: le.toId,
    toInterfacePath: le.toInterfaceKey.split("."),
    source: "process",
  };
}
